import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import PopupCard from "../../../../Auth/PopupCard";
import ImportUI from "../../Users/DailogBoxes/ImportUI";
import LocationMapping from "../Location/LocationMapping";
import {
  bulkImportDepartment,
  fetchAllDepartments,
  importDepartment,
} from "../../../../../redux/OrganizationSetup/organizationSetupSlice";

const supportedTypes = [".csv"];

const normalizeLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getRowValueByHeader = (row, header) => {
  if (!row || typeof row !== "object") {
    return "";
  }

  if (Object.prototype.hasOwnProperty.call(row, header)) {
    return row[header];
  }

  const normalizedHeader = normalizeLabel(header);
  const matchedKey = Object.keys(row).find(
    (key) => normalizeLabel(key) === normalizedHeader,
  );

  return matchedKey ? row[matchedKey] : "";
};

const buildUniqueList = (values) =>
  values.reduce((acc, value) => {
    if (!value) return acc;
    if (!acc.some((item) => normalizeLabel(item) === normalizeLabel(value))) {
      acc.push(value);
    }
    return acc;
  }, []);

const getHeaderList = (payload) =>
  buildUniqueList(
    []
      .concat(Array.isArray(payload?.csvHeaders) ? payload.csvHeaders : [])
      .concat(Array.isArray(payload?.headers) ? payload.headers : []),
  );

const getPreviewRows = (payload) => {
  if (Array.isArray(payload?.sampleData)) {
    return payload.sampleData.filter(
      (row) => row && typeof row === "object" && !Array.isArray(row),
    );
  }

  if (Array.isArray(payload?.sampleRows)) {
    return payload.sampleRows.filter(
      (row) => row && typeof row === "object" && !Array.isArray(row),
    );
  }

  if (Array.isArray(payload?.mappedPreview)) {
    return payload.mappedPreview.filter(
      (row) => row && typeof row === "object" && !Array.isArray(row),
    );
  }

  return [];
};

const buildMappingRows = (response) => {
  const payload = response?.data || {};
  const fileHeaders = getHeaderList(payload);
  const previewRows = getPreviewRows(payload);
  const attributeOptions = buildUniqueList(
    Array.isArray(payload?.dbFields)
      ? payload.dbFields
      : Array.isArray(payload?.headers)
        ? payload.headers
        : fileHeaders,
  );
  const suggestedMappings =
    payload?.suggestedMappings && typeof payload.suggestedMappings === "object"
      ? payload.suggestedMappings
      : {};

  const rows = fileHeaders.map((header) => ({
    header,
    samples: previewRows
      .map((row) => getRowValueByHeader(row, header))
      .filter(
        (item) => item !== undefined && item !== null && String(item).trim(),
      )
      .slice(0, 3)
      .map((item) => String(item)),
    suggestedAttribute: suggestedMappings[header] || "",
  }));

  return {
    rows,
    attributeOptions,
    selections: rows.reduce((acc, row) => {
      const matchedAttribute =
        suggestedMappings[row.header] ||
        attributeOptions.find(
          (option) => normalizeLabel(option) === normalizeLabel(row.header),
        ) ||
        "";

      acc[row.header] = matchedAttribute;
      return acc;
    }, {}),
  };
};

const formatApiError = (error, fallbackMessage) => {
  if (typeof error === "string") {
    return error;
  }

  const errors = Array.isArray(error?.errors)
    ? error.errors.filter(Boolean)
    : [];

  if (errors.length > 0) {
    return errors.join(", ");
  }

  return error?.message || fallbackMessage;
};

const getApiErrors = (error, fallbackMessage) => {
  if (typeof error === "string") {
    return [error];
  }

  const errors = Array.isArray(error?.errors)
    ? error.errors.filter((item) => item && String(item).trim())
    : [];

  if (errors.length > 0) {
    return errors.map((item) => String(item));
  }

  if (error?.message) {
    return [String(error.message)];
  }

  return [fallbackMessage];
};

const getMappedSelections = (mappingSelections) =>
  Object.entries(mappingSelections).reduce((acc, [header, attribute]) => {
    if (typeof attribute === "string" && attribute.trim()) {
      acc[header] = attribute;
    }

    return acc;
  }, {});

const ImportDepartment = ({
  onClose,
  isMapping = false,
  heading = "Import Department",
  description = "Upload your CSV, validate the data, and map the columns before import.",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isMappingStep =
    isMapping || routerLocation.pathname.endsWith("/mapping");
  const routeState = routerLocation.state || {};
  const { loadingParams = {}, bulkDepartmentData } = useSelector(
    (state) => state.orgSetup || {},
  );
  const inputRef = useRef(null);
  const [documentName, setDocumentName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFileValid, setIsFileValid] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const [mappingRows, setMappingRows] = useState([]);
  const [mappingSelections, setMappingSelections] = useState({});
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [currentStep, setCurrentStep] = useState("upload");
  const [popupConfig, setPopupConfig] = useState(null);
  const [hasSubmittedImport, setHasSubmittedImport] = useState(false);
  const [importResultData, setImportResultData] = useState(null);
  const isUploadingDepartment = Boolean(loadingParams.departmentImport);
  const isImporting = Boolean(loadingParams.bulkImportLoad);

  const openFileDialog = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  const showErrorPopup = (title, error, fallbackMessage) => {
    const errors = getApiErrors(error, fallbackMessage);

    setPopupConfig({
      type: "error",
      title,
      message: (
        <div className="space-y-2 text-left">
          {errors.map((item, index) => (
            <p key={`${title}_error_${index}`} className="text-sm leading-6">
              {item}
            </p>
          ))}
        </div>
      ),
    });
  };

  const buildFileFormData = (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return formData;
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    setSelectedFile(file);
    setIsFileValid(true);
    setCurrentStep("upload");
    setValidationMessage("");
    setValidationError("");
    setMappingRows([]);
    setMappingSelections({});
    setAttributeOptions([]);
    setHasSubmittedImport(false);
    setImportResultData(null);
    setDocumentName(file.name.replace(/\.[^/.]+$/, ""));
  };

  const handleInputChange = (event) => {
    handleFileSelect(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileSelect(event.dataTransfer.files?.[0]);
  };

  const handleNext = async () => {
    if (!selectedFile) return;

    setValidationMessage("");
    setValidationError("");

    try {
      const response = await dispatch(
        importDepartment(buildFileFormData(selectedFile)),
      ).unwrap();
      const mappingData = buildMappingRows(response);

      navigate("/organization/organization-setup/import-department/mapping", {
        state: {
          selectedFile,
          documentName,
          uploadResponse: response,
          mappingData,
        },
      });
    } catch (error) {
      setIsFileValid(false);
      setMappingRows([]);
      setAttributeOptions([]);
      setMappingSelections({});
      const errorMessage = formatApiError(
        error,
        "Department file upload failed.",
      );
      setValidationError(errorMessage);
      showErrorPopup("Upload Failed", error, "Department file upload failed.");
    }
  };

  useEffect(() => {
    if (isMappingStep) {
      if (routeState?.uploadResponse && routeState?.selectedFile) {
        setSelectedFile(routeState.selectedFile);
        setDocumentName(
          routeState.documentName ||
            routeState.selectedFile.name.replace(/\.[^/.]+$/, ""),
        );
        const mappingData =
          routeState.mappingData || buildMappingRows(routeState.uploadResponse);
        setMappingRows(mappingData.rows);
        setAttributeOptions(mappingData.attributeOptions);
        setMappingSelections(mappingData.selections);
        setIsFileValid(mappingData.rows.length > 0);
      } else {
        navigate("/organization/organization-setup/import-department", {
          replace: true,
        });
      }
    }
  }, [isMappingStep, routeState]);

  const handleMappingSelectionChange = (header, value) => {
    setMappingSelections((prev) => ({
      ...prev,
      [header]: value,
    }));
    setImportResultData(null);
  };

  const handleImport = async () => {
    setPopupConfig({
      type: "warning",
      title: "Confirm Import",
      message: "Do you want to import these mapped departments?",
      onConfirm: async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append(
          "columnMapping",
          JSON.stringify(getMappedSelections(mappingSelections)),
        );

        setValidationError("");
        setHasSubmittedImport(true);
        setPopupConfig(null);

        try {
          await dispatch(bulkImportDepartment(formData)).unwrap();
        } catch (error) {
          setHasSubmittedImport(false);
          const errorMessage = formatApiError(
            error,
            "Department import failed.",
          );
          setValidationError(errorMessage);
          showErrorPopup("Import Failed", error, "Department import failed.");
        }
      },
      onCancel: () => setPopupConfig(null),
    });
  };

  useEffect(() => {
    if (!hasSubmittedImport || !bulkDepartmentData?.success) {
      return;
    }

    setImportResultData(bulkDepartmentData);

    const importPayload = bulkDepartmentData?.data || {};
    const importErrors = Array.isArray(importPayload?.errors)
      ? importPayload.errors
      : [];
    const validationErrors = Array.isArray(importPayload?.validationErrors)
      ? importPayload.validationErrors
      : [];
    const hasImportErrors =
      importErrors.length > 0 ||
      validationErrors.length > 0 ||
      Number(importPayload?.failedRows || 0) > 0 ||
      Number(importPayload?.failedCount || 0) > 0;

    setPopupConfig({
      type: "success",
      title: "Success",
      message:
        bulkDepartmentData?.message || "Departments imported successfully.",
    });

    const timeoutId = window.setTimeout(() => {
      setPopupConfig(null);
      setHasSubmittedImport(false);
      if (!hasImportErrors) {
        dispatch(fetchAllDepartments());
        onClose();
      }
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [bulkDepartmentData, dispatch, hasSubmittedImport, onClose]);

  useEffect(() => {
    if (!popupConfig || popupConfig.type === "warning") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setPopupConfig(null);
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [popupConfig]);

  const handleBack = () => {
    if (isMappingStep) {
      navigate("/organization/organization-setup/import-department");
      return;
    }

    onClose();
  };

  return (
    <>
      <ImportUI
        heading={heading}
        description={description}
        downloadHref="/samples/department-import-sample.csv"
        downloadName="department-import-sample.csv"
        onBack={handleBack}
        inputRef={inputRef}
        accept={supportedTypes.join(",")}
        onInputChange={handleInputChange}
        showUploader={!isMappingStep}
        openFileDialog={openFileDialog}
        isDragging={isDragging}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        selectedFile={selectedFile}
        documentName={documentName}
        supportedTypes={supportedTypes}
        onCancel={onClose}
        onNext={handleNext}
        nextDisabled={!selectedFile || isUploadingDepartment}
        nextLabel={isUploadingDepartment ? "Uploading..." : "Next"}
      >
        <LocationMapping
          mappingRows={mappingRows}
          mappingSelections={mappingSelections}
          attributeOptions={attributeOptions}
          onSelectionChange={handleMappingSelectionChange}
          onCancel={() =>
            navigate("/organization/organization-setup/import-department")
          }
          onImport={handleImport}
          importResult={importResultData}
          importButtonLabel="Import Departments"
          backButtonLabel="Back"
          isImporting={isImporting}
          validationError={validationError}
        />
      </ImportUI>
      {popupConfig && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 px-4">
          <PopupCard
            type={popupConfig.type}
            title={popupConfig.title}
            message={popupConfig.message}
            onConfirm={popupConfig.onConfirm}
            onCancel={popupConfig.onCancel}
            confirmText={popupConfig.confirmText}
            cancelText={popupConfig.cancelText}
          />
        </div>
      )}
    </>
  );
};

export default ImportDepartment;
